/**
 * @fileoverview Organization Context for managing multi-tenant organization configuration and branding.
 *
 * This context provides comprehensive organization management functionality including:
 * - Multi-tenant subdomain detection and routing
 * - Organization-specific configuration loading
 * - Dynamic branding and theming
 * - API URL configuration based on environment
 * - CSS custom property injection for theming
 * - Organization-specific page titles and metadata
 *
 * Used throughout the application for:
 * - Multi-tenant organization isolation
 * - Dynamic branding and color schemes
 * - Organization-specific API endpoints
 * - Custom styling and theming
 * - Subdomain-based routing logic
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * React Context for organization configuration and branding
 * @type {React.Context<OrganizationContextValue>}
 */
const OrganizationContext = createContext();

/**
 * Custom hook to access the organization context.
 * Must be used within an OrganizationProvider component.
 *
 * @function useOrganization
 * @returns {OrganizationContextValue} The organization context value containing config and methods
 * @throws {Error} When used outside of OrganizationProvider
 *
 * @example
 * ```jsx
 * function Header() {
 *   const { organizationConfig, isOrganizationSubdomain } = useOrganization();
 *
 *   return (
 *     <header>
 *       <h1>{organizationConfig?.name || 'Clockwise CPA'}</h1>
 *       {isOrganizationSubdomain && (
 *         <img src={organizationConfig?.branding?.logo} alt="Logo" />
 *       )}
 *     </header>
 *   );
 * }
 * ```
 */
export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};

/**
 * Organization Provider component that manages organization configuration and branding.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to organization context
 * @returns {JSX.Element} Provider component wrapping children with organization context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <OrganizationProvider>
 *       <Router>
 *         <Routes>
 *           <Route path="/dashboard" element={<Dashboard />} />
 *         </Routes>
 *       </Router>
 *     </OrganizationProvider>
 *   );
 * }
 * ```
 */
export const OrganizationProvider = ({ children }) => {
    const [organizationConfig, setOrganizationConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentOrganization, setCurrentOrganization] = useState(null);

    /**
     * Extracts organization identifier from hostname subdomain or dev context
     * @returns {string|null} Organization identifier or null if none found
     */
    const getOrganizationFromHostname = () => {
        if (typeof window === 'undefined') return null;

        const hostname = window.location.hostname;
        const parts = hostname.split('.');

        // Check for development subdomain pattern (e.g., fart.localhost)
        if (hostname.endsWith('.localhost') || hostname.includes('.127.0.0.1')) {
            const subdomain = parts[0];
            const reserved = ['www', 'api', 'fileapi', 'timefiles'];

            // Admin subdomain should redirect to backend directly
            if (subdomain === 'admin') {
                window.location.href = `http://admin.localhost:5050${window.location.pathname}`;
                return null;
            }

            if (!reserved.includes(subdomain)) {
                return subdomain;
            }
        }

        // Check for development organization context override (plain localhost)
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            // In development, check for dev organization context
            const devOrgSubdomain = localStorage.getItem('devOrgSubdomain');
            if (devOrgSubdomain && devOrgSubdomain !== 'main') {
                return devOrgSubdomain;
            }
            return null;
        }

        // Check if it's a subdomain pattern (production)
        if (parts.length >= 3) {
            const subdomain = parts[0];

            // Skip reserved subdomains
            const reserved = ['www', 'api', 'admin', 'fileapi', 'timefiles'];
            if (!reserved.includes(subdomain)) {
                return subdomain;
            }
        }

        return null;
    };

    /**
     * Checks if current request is from an organization subdomain
     * @returns {boolean} True if on organization subdomain
     */
    const isOrganizationSubdomain = () => {
        return getOrganizationFromHostname() !== null;
    };

    /**
     * Gets the appropriate API URL based on current environment
     * @returns {string} The API URL to use for requests
     */
    const getApiUrl = () => {
        // Default based on environment
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;

            // Development: Use same hostname but port 5050 for API
            // This keeps cookies same-site (demo.localhost:3000 -> demo.localhost:5050)
            if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
                return `http://${hostname}:5050`;
            }

            // Production: use api.clockwisecpa.app (handles all subdomains)
            return 'https://api.clockwisecpa.app';
        }

        // Fallback for SSR or non-browser environments
        return 'https://api.clockwisecpa.app';
    };

    useEffect(() => {
        const loadOrganizationConfig = async () => {
            setIsLoading(true);

            const organizationId = getOrganizationFromHostname();
            if (process.env.NODE_ENV === 'development' && organizationId) {
                console.log('🏢 OrganizationContext: detected org =', organizationId);
            }
            setCurrentOrganization(organizationId);

            if (organizationId) {
                try {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🏢 Fetching organization config for:', organizationId);
                    }
                    const response = await fetch(`${getApiUrl()}/api/organization/config`, {
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Organization-ID': organizationId,  // Pass org to backend for validation
                        },
                    });
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🏢 Organization config response:', response.status);
                    }
                    
                    if (response.ok) {
                        const config = await response.json();
                        setOrganizationConfig(config);

                        // Apply organization branding
                        if (config.branding) {
                            document.documentElement.style.setProperty('--organization-primary-color', config.branding.primaryColor);
                            document.documentElement.style.setProperty('--organization-secondary-color', config.branding.secondaryColor);

                            // Calculate hover and light variants
                            const primaryColor = config.branding.primaryColor;
                            if (primaryColor) {
                                // Create hover variant (slightly darker)
                                const rgb = hexToRgb(primaryColor);
                                if (rgb) {
                                    const hoverRgb = {
                                        r: Math.max(0, rgb.r - 20),
                                        g: Math.max(0, rgb.g - 20),
                                        b: Math.max(0, rgb.b - 20)
                                    };
                                    document.documentElement.style.setProperty('--organization-primary-hover', `rgb(${hoverRgb.r}, ${hoverRgb.g}, ${hoverRgb.b})`);

                                    // Create light variant (with transparency)
                                    document.documentElement.style.setProperty('--organization-primary-light', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`);
                                    document.documentElement.style.setProperty('--organization-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
                                }
                            }

                        }
                    } else if (response.status === 404) {
                        // Organization not found - redirect to main site
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Organization not found, redirecting to main site');
                        }
                        const hostname = window.location.hostname;
                        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
                            // Dev: redirect to localhost without subdomain
                            window.location.href = `http://localhost:${window.location.port || 3000}/login`;
                        } else {
                            // Prod: redirect to main site
                            window.location.href = 'https://www.clockwisecpa.app';
                        }
                        return;
                    } else {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Failed to load organization config:', response.status);
                        }
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Failed to load organization config:', error);
                    }
                }
            } else {
                // Main domain - clear any organization config
                setOrganizationConfig(null);
                
                // Reset CSS variables to defaults
                document.documentElement.style.setProperty('--organization-primary-color', '#007bff');
                document.documentElement.style.setProperty('--organization-secondary-color', '#6c757d');
                document.documentElement.style.setProperty('--organization-primary-hover', '#0056b3');
                document.documentElement.style.setProperty('--organization-primary-light', 'rgba(0, 123, 255, 0.1)');
                document.documentElement.style.setProperty('--organization-primary-rgb', '0, 123, 255');
            }
            
            setIsLoading(false);
        };

        loadOrganizationConfig();

        // Listen for dev organization context changes
        const handleStorageChange = (e) => {
            if (e.key === 'devOrgSubdomain' || e.key === 'devOrgContext') {
                loadOrganizationConfig();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    /**
     * Converts hex color to RGB object
     * @param {string} hex - Hex color string (e.g., '#FF0000')
     * @returns {Object|null} RGB object with r, g, b properties or null if invalid
     */
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    /**
     * Check whether a feature is enabled for the current organization.
     *
     * Features are opt-in: an absent key means the feature is off, so enabling
     * one for a single organization never exposes it to the others. Display
     * only -- the server enforces the same flag on the routes themselves.
     *
     * @param {string} featureKey - Key from the organization's features map
     * @returns {boolean}
     */
    const hasFeature = (featureKey) => {
        if (!featureKey) return false;
        return organizationConfig?.features?.[featureKey] === true;
    };

    /**
     * The context value object containing all organization state and methods
     * @typedef {Object} OrganizationContextValue
     * @property {Object|null} organizationConfig - Current organization configuration
     * @property {string|null} currentOrganization - Current organization identifier
     * @property {boolean} isLoading - Whether organization config is loading
     * @property {boolean} isOrganizationSubdomain - Whether on organization subdomain
     * @property {Function} hasFeature - Check if an org feature flag is enabled
     * @property {Function} refreshOrganizationConfig - Refresh organization configuration
     * @property {Function} getOrganizationFromHostname - Extract organization from hostname
     * @property {Function} getApiUrl - Get appropriate API URL for environment
     */
    const value = {
        organizationConfig,
        hasFeature,
        currentOrganization,
        isLoading,
        isOrganizationSubdomain: isOrganizationSubdomain(),
        refreshOrganizationConfig: async () => {
            // Reload organization config without full page reload
            setIsLoading(true);

            const organizationId = getOrganizationFromHostname();
            setCurrentOrganization(organizationId);

            if (organizationId) {
                try {
                    const response = await fetch(`${getApiUrl()}/api/organization/config`, {
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });

                    if (response.ok) {
                        const config = await response.json();
                        setOrganizationConfig(config);

                        // Apply organization branding
                        if (config.branding) {
                            document.documentElement.style.setProperty('--organization-primary-color', config.branding.primaryColor);
                            document.documentElement.style.setProperty('--organization-secondary-color', config.branding.secondaryColor);

                            // Calculate hover and light variants
                            const primaryColor = config.branding.primaryColor;
                            if (primaryColor) {
                                const rgb = hexToRgb(primaryColor);
                                if (rgb) {
                                    const hoverRgb = {
                                        r: Math.max(0, rgb.r - 20),
                                        g: Math.max(0, rgb.g - 20),
                                        b: Math.max(0, rgb.b - 20)
                                    };
                                    document.documentElement.style.setProperty('--organization-primary-hover', `rgb(${hoverRgb.r}, ${hoverRgb.g}, ${hoverRgb.b})`);
                                    document.documentElement.style.setProperty('--organization-primary-light', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`);
                                    document.documentElement.style.setProperty('--organization-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
                                }
                            }
                        }
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Failed to refresh organization config:', error);
                    }
                }
            } else {
                setOrganizationConfig(null);

                // Reset CSS variables to defaults
                document.documentElement.style.setProperty('--organization-primary-color', '#007bff');
                document.documentElement.style.setProperty('--organization-secondary-color', '#6c757d');
                document.documentElement.style.setProperty('--organization-primary-hover', '#0056b3');
                document.documentElement.style.setProperty('--organization-primary-light', 'rgba(0, 123, 255, 0.1)');
                document.documentElement.style.setProperty('--organization-primary-rgb', '0, 123, 255');
            }

            setIsLoading(false);
        },
        getOrganizationFromHostname,
        getApiUrl
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};

